import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Car, Search, SlidersHorizontal, X, Menu, ChevronLeft, ChevronRight,
  Building2, Fuel, Settings, Calendar, ArrowRight, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: '#050A30', bgAlt: '#0A1240', bgDeep: '#020818',
  cyan: '#00FFFF', gold: '#FBBF24', white: '#FFFFFF',
  textMuted: '#A0A8C8', cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: 'rgba(0,255,255,0.12)',
  font: {
    display: "'Barlow Condensed', sans-serif",
    heading: "'Barlow', sans-serif",
    body: "'Inter', sans-serif",
  }
};

const fmt = (price, currency = 'ARS') => {
  if (!price || price === 0) return 'Consultar';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
};
const fuelLabel = (f) => ({ nafta: 'Nafta', diesel: 'Diesel', gnc: 'GNC', electrico: 'Electrico', hibrido: 'Hibrido' }[f] || f || '-');
const transLabel = (t) => ({ manual: 'Manual', automatic: 'Automatico' }[t] || t || '-');
const fmtKm = (km) => km ? new Intl.NumberFormat('es-AR').format(km) + ' km' : '-';

export default function AutosFlowPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  const [filters, setFilters] = useState({ marca: '', modelo: '', anio_min: '', anio_max: '', precio_min: '', precio_max: '' });

  const fetchVehicles = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    fetch(`${API_URL}/api/public/vehicles?${params}`)
      .then(r => r.json())
      .then(d => { setVehicles(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const clearFilters = () => setFilters({ marca: '', modelo: '', anio_min: '', anio_max: '', precio_min: '', precio_max: '' });

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const openModal = (v) => { setSelected(v); setImgIdx(0); };
  const closeModal = () => setSelected(null);

  const allImages = selected ? [selected.media?.coverUrl, ...(selected.media?.images || [])].filter(Boolean) : [];

  return (
    <div style={{ background: T.bg, fontFamily: T.font.body, color: T.white }} className="min-h-screen flex flex-col">
      <Helmet>
        <title>Autos - Flow | Todos los vehiculos</title>
        <meta property="og:title" content="Autos - Flow | Marketplace de vehiculos" />
        <meta property="og:description" content="Todos los vehiculos disponibles en la red AutoFlow" />
      </Helmet>

      {/* HEADER */}
      <header className="sticky top-0 z-50" style={{ background: 'rgba(5,10,48,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="autosflow-logo">
            <img src="/logo.png" alt="AutoFlow" className="h-9 w-auto rounded-lg" />
            <span style={{ fontFamily: T.font.display, fontWeight: 800, fontSize: '1.3rem' }}>Auto<span style={{ color: T.cyan }}>Flow</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/autos-flow" className="text-sm font-bold" style={{ color: T.cyan }}>Autos - Flow</Link>
            <Link to="/agencias" className="text-sm" style={{ color: T.textMuted }}>Agencias</Link>
            <Link to="/" className="text-sm" style={{ color: T.textMuted }}>Inicio</Link>
            <Link to="/app/login" className="text-sm px-5 py-2 rounded-full" style={{ border: `1.5px solid ${T.cyan}`, color: T.cyan }}>Ingresar</Link>
          </nav>
          <button className="md:hidden p-2 min-h-[44px] min-w-[44px]" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-5 py-4 space-y-3" style={{ background: 'rgba(5,10,48,0.97)', borderBottom: `1px solid ${T.cardBorder}` }}>
            <Link to="/autos-flow" className="block py-2 font-bold" style={{ color: T.cyan }}>Autos - Flow</Link>
            <Link to="/agencias" className="block py-2" style={{ color: T.textMuted }}>Agencias</Link>
            <Link to="/" className="block py-2" style={{ color: T.textMuted }}>Inicio</Link>
            <Link to="/app/login" className="block text-center py-3 rounded-full font-semibold mt-2" style={{ background: T.cyan, color: T.bg }}>Ingresar</Link>
          </div>
        )}
      </header>

      {/* HERO + FILTERS */}
      <section className="py-10 md:py-16 text-center">
        <div className="max-w-4xl mx-auto px-5">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3" style={{ fontFamily: T.font.display, fontWeight: 800 }}>
            Autos - Flow
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="text-base md:text-lg mb-8" style={{ color: T.textMuted }}>
            Todos los vehiculos disponibles en la red AutoFlow
          </motion.p>
        </div>

        {/* Filter Bar */}
        <div className="max-w-5xl mx-auto px-5">
          <div className="rounded-2xl p-4 md:p-5" style={{ background: T.bgAlt, border: `1px solid ${T.cardBorder}` }}>
            {/* Desktop Filters */}
            <div className="hidden md:grid md:grid-cols-6 gap-3 items-end">
              <FilterInput label="Marca" value={filters.marca} onChange={v => setFilters(p => ({ ...p, marca: v }))} placeholder="Toyota..." />
              <FilterInput label="Modelo" value={filters.modelo} onChange={v => setFilters(p => ({ ...p, modelo: v }))} placeholder="Corolla..." />
              <FilterInput label="Ano min" value={filters.anio_min} onChange={v => setFilters(p => ({ ...p, anio_min: v }))} placeholder="2018" type="number" />
              <FilterInput label="Ano max" value={filters.anio_max} onChange={v => setFilters(p => ({ ...p, anio_max: v }))} placeholder="2025" type="number" />
              <FilterInput label="Precio min" value={filters.precio_min} onChange={v => setFilters(p => ({ ...p, precio_min: v }))} placeholder="5.000.000" type="number" />
              <FilterInput label="Precio max" value={filters.precio_max} onChange={v => setFilters(p => ({ ...p, precio_max: v }))} placeholder="30.000.000" type="number" />
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="hidden md:inline-flex items-center gap-1 mt-3 text-xs hover:opacity-80" style={{ color: T.cyan }}>
                <X size={12} /> Limpiar filtros ({activeFilterCount})
              </button>
            )}

            {/* Mobile Filter Toggle */}
            <div className="md:hidden">
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium"
                style={{ background: 'rgba(0,255,255,0.08)', color: T.cyan }}
                data-testid="mobile-filter-toggle"
              >
                <SlidersHorizontal size={16} />
                Filtros {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              {filtersOpen && (
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <FilterInput label="Marca" value={filters.marca} onChange={v => setFilters(p => ({ ...p, marca: v }))} placeholder="Toyota..." />
                  <FilterInput label="Modelo" value={filters.modelo} onChange={v => setFilters(p => ({ ...p, modelo: v }))} placeholder="Corolla..." />
                  <FilterInput label="Ano min" value={filters.anio_min} onChange={v => setFilters(p => ({ ...p, anio_min: v }))} placeholder="2018" type="number" />
                  <FilterInput label="Ano max" value={filters.anio_max} onChange={v => setFilters(p => ({ ...p, anio_max: v }))} placeholder="2025" type="number" />
                  <FilterInput label="$ min" value={filters.precio_min} onChange={v => setFilters(p => ({ ...p, precio_min: v }))} placeholder="5M" type="number" />
                  <FilterInput label="$ max" value={filters.precio_max} onChange={v => setFilters(p => ({ ...p, precio_max: v }))} placeholder="30M" type="number" />
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="col-span-2 flex items-center justify-center gap-1 py-2 text-xs" style={{ color: T.cyan }}>
                      <X size={12} /> Limpiar filtros
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* VEHICLES GRID */}
      <section className="pb-20 md:pb-28 flex-1">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          {loading ? (
            <div className="text-center py-16" style={{ color: T.textMuted }}>
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              Cargando vehiculos...
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-16">
              <Car className="mx-auto mb-4 opacity-30" size={48} />
              <p style={{ color: T.textMuted }}>No se encontraron vehiculos con esos filtros</p>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="mt-4 text-sm underline" style={{ color: T.cyan }}>Limpiar filtros</button>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm mb-6" style={{ color: T.textMuted }}>{vehicles.length} vehiculo{vehicles.length !== 1 ? 's' : ''} encontrado{vehicles.length !== 1 ? 's' : ''}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vehicles-grid">
                {vehicles.map((v, i) => {
                  const veh = v.vehicle || {};
                  const price = v.pricing?.price;
                  const cover = v.media?.coverUrl;
                  return (
                    <motion.div
                      key={v.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03, duration: 0.35 }}
                      onClick={() => openModal(v)}
                      className="cursor-pointer group"
                      data-testid={`vehicle-card-${v.id}`}
                    >
                      <div className="rounded-2xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
                        <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
                          {cover ? (
                            <img src={cover} alt={`${veh.make} ${veh.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: T.bgAlt }}>
                              <Car size={40} color={T.textMuted} />
                            </div>
                          )}
                          {v.agency_name && (
                            <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', color: T.white }}>
                              {v.agency_logo ? (
                                <img src={v.agency_logo} alt="" className="w-4 h-4 rounded-full object-cover" />
                              ) : (
                                <Building2 size={10} />
                              )}
                              {v.agency_name}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm md:text-base font-bold truncate" style={{ fontFamily: T.font.heading }}>
                            {veh.make} {veh.model}
                          </h3>
                          <div className="flex items-center gap-3 mt-1.5 text-xs" style={{ color: T.textMuted }}>
                            {veh.year && <span className="flex items-center gap-1"><Calendar size={11} /> {veh.year}</span>}
                            {veh.fuel && <span className="flex items-center gap-1"><Fuel size={11} /> {fuelLabel(veh.fuel)}</span>}
                            {veh.transmission && <span className="flex items-center gap-1"><Settings size={11} /> {transLabel(veh.transmission)}</span>}
                          </div>
                          <p className="mt-3 text-lg font-bold" style={{ fontFamily: T.font.display, color: T.cyan }}>
                            {fmt(price, v.pricing?.currency)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* VEHICLE DETAIL MODAL */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl"
              style={{ background: T.bgAlt, border: `1px solid ${T.cardBorder}` }}
              onClick={e => e.stopPropagation()}
              data-testid="vehicle-detail-modal"
            >
              {/* Gallery */}
              <div className="relative aspect-video bg-black">
                {allImages.length > 0 ? (
                  <img src={allImages[imgIdx]} alt="" className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Car size={60} color={T.textMuted} /></div>
                )}
                <button onClick={closeModal} className="absolute top-3 right-3 p-2 rounded-full" style={{ background: 'rgba(0,0,0,0.6)' }} data-testid="close-vehicle-modal">
                  <X size={20} />
                </button>
                {allImages.length > 1 && (
                  <>
                    <button onClick={() => setImgIdx(p => (p - 1 + allImages.length) % allImages.length)} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full" style={{ background: 'rgba(0,0,0,0.6)' }}>
                      <ChevronLeft size={20} />
                    </button>
                    <button onClick={() => setImgIdx(p => (p + 1) % allImages.length)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full" style={{ background: 'rgba(0,0,0,0.6)' }}>
                      <ChevronRight size={20} />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {allImages.map((_, i) => (
                        <button key={i} onClick={() => setImgIdx(i)} className="w-2 h-2 rounded-full transition-all" style={{ background: i === imgIdx ? T.cyan : 'rgba(255,255,255,0.4)' }} />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Info */}
              <div className="p-5 md:p-7">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: T.font.display }}>
                      {selected.vehicle?.make} {selected.vehicle?.model}
                    </h2>
                    <p className="text-sm mt-1" style={{ color: T.textMuted }}>
                      {selected.vehicle?.year} &middot; {fuelLabel(selected.vehicle?.fuel)} &middot; {transLabel(selected.vehicle?.transmission)}
                    </p>
                  </div>
                  <p className="text-2xl font-bold whitespace-nowrap" style={{ fontFamily: T.font.display, color: T.cyan }}>
                    {fmt(selected.pricing?.price, selected.pricing?.currency)}
                  </p>
                </div>

                {/* Specs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Kilometraje', value: fmtKm(selected.vehicle?.mileage) },
                    { label: 'Color', value: selected.vehicle?.color || '-' },
                    { label: 'Condicion', value: selected.condition === 'new' ? 'Nuevo' : 'Usado' },
                    { label: 'Puertas', value: selected.vehicle?.doors || '-' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-xl text-center" style={{ background: 'rgba(0,255,255,0.04)', border: `1px solid ${T.cardBorder}` }}>
                      <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: T.textMuted }}>{s.label}</p>
                      <p className="text-sm font-semibold">{s.value}</p>
                    </div>
                  ))}
                </div>

                {selected.description && (
                  <div className="mb-6">
                    <h4 className="text-sm font-bold mb-2" style={{ fontFamily: T.font.heading }}>Descripcion</h4>
                    <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>{selected.description}</p>
                  </div>
                )}

                {/* Agency Section */}
                {selected.agency_name && (
                  <div className="p-4 rounded-xl flex flex-col sm:flex-row items-center gap-4" style={{ background: 'rgba(0,255,255,0.04)', border: `1px solid ${T.cardBorder}` }} data-testid="vehicle-agency-section">
                    {selected.agency_logo ? (
                      <img src={selected.agency_logo} alt={selected.agency_name} className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: T.cyan }} />
                    ) : (
                      <div className="w-14 h-14 rounded-full flex items-center justify-center border-2" style={{ background: 'rgba(0,255,255,0.06)', borderColor: 'rgba(0,255,255,0.3)' }}>
                        <Building2 size={24} color={T.cyan} />
                      </div>
                    )}
                    <div className="flex-1 text-center sm:text-left">
                      <p className="font-bold" style={{ fontFamily: T.font.heading }}>{selected.agency_name}</p>
                      <p className="text-xs mt-0.5" style={{ color: T.textMuted }}>Agencia verificada en AutoFlow</p>
                    </div>
                    <Link
                      to={`/a/${selected.agency_slug}`}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:brightness-110"
                      style={{ background: T.gold, color: '#1a1a2e' }}
                      data-testid="modal-view-agency"
                    >
                      Ver agencia <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <footer className="py-8 border-t" style={{ background: T.bgDeep, borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs" style={{ color: T.textMuted }}>&copy; {new Date().getFullYear()} AutoFlow</span>
          <div className="flex items-center gap-4 text-xs" style={{ color: T.textMuted }}>
            <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
            <Link to="/agencias" className="hover:text-white transition-colors">Agencias</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacidad</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terminos</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FilterInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest mb-1.5 font-medium" style={{ color: '#A0A8C8' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all focus:ring-1"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(0,255,255,0.12)',
          color: '#fff',
        }}
        data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`}
      />
    </div>
  );
}
